import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Shield, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";

const EFFECTIVE_DATE = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

// ── Role-specific checkboxes ────────────────────────────────────────────────
const RECRUITER_CHECKS = [
  { id: "rc_status",    label: "I understand this is an independent contractor (B2B freelance) relationship. I am NOT an employee, agent, or partner of Hylas Companion. I am free to work for other clients with no exclusivity." },
  { id: "rc_comp",      label: "I understand compensation is performance-based — paid per successfully placed driver. Standard plan: Our Leads 50% / Your Leads 75% depending on lead source. Weekly Flex: 25% of carrier weekly rate ($14–$94/week), recurring for as long as the driver stays active. No hourly wage, no base salary." },
  { id: "rc_yourleads", label: "I understand the 'Your Leads' 75% tier applies ONLY when I independently sourced and initiated contact with the driver BEFORE entering the Hylas pipeline. Misrepresenting lead source is a material breach of this agreement." },
  { id: "rc_referral",  label: "I agree NOT to offer referral bonuses to drivers. Hylas operates an exclusive in-house driver referral program. Unauthorized bonuses are a violation of this agreement." },
  { id: "rc_conf",      label: "I agree to maintain strict confidentiality of all driver profiles, carrier contacts, pay rates, the Hylas Matrix, call scripts, and all Market data — during and for 12 months after this agreement ends." },
];

const SETTER_CHECKS = [
  { id: "sc_status",    label: "I understand this is an independent contractor (B2B freelance) relationship. I am NOT an employee, agent, or partner of Hylas Companion. I am free to work for other clients with no exclusivity." },
  { id: "sc_comp",      label: "I understand compensation is performance-based — paid per successfully hired driver. Standard rates: $396–$726 depending on equipment and pay program. Weekly Flex: 15% of carrier weekly rate ($8–$56/week), recurring for as long as the driver stays active. No hourly wage, no base salary." },
  { id: "sc_nohire",    label: "I understand 'no hire = no pay.' No fee is earned for canceled, no-show, or unqualified appointments." },
  { id: "sc_referral",  label: "I agree NOT to offer referral bonuses to drivers. Hylas operates an exclusive in-house driver referral program. Unauthorized bonuses are a violation of this agreement." },
  { id: "sc_conf",      label: "I agree to maintain confidentiality of all carrier lists, pricing, the Hylas Matrix, and sales methodology — during and for 12 months after this agreement ends." },
];

export function StaffAgreementModal({ token, hub, staffName, onAgreed, onClose }) {
  const isRecruiter = hub === "recruiter";
  const CHECKBOXES  = isRecruiter ? RECRUITER_CHECKS : SETTER_CHECKS;
  const [checked, setChecked]         = useState({});
  const [signConsent, setSignConsent] = useState(false);
  const [sigName, setSigName]         = useState(staffName || "");
  const [submitting, setSubmitting]   = useState(false);
  const scrollRef                     = useRef(null);

  const allChecked = CHECKBOXES.every(c => checked[c.id]);
  const canSign    = allChecked && signConsent && sigName.trim().length >= 2;

  async function handleSign() {
    if (!canSign) return;
    setSubmitting(true);
    try {
      const res = await api.marketSignStaffAgreement(token, { signature_name: sigName.trim() });
      toast.success("Agreement signed — welcome to the team!");
      onAgreed({ signed_at: res.signed_at || new Date().toISOString(), signature_name: sigName.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed — please try again.");
    } finally { setSubmitting(false); }
  }

  const Role   = isRecruiter ? "Full-Cycle Recruiter" : "Appointment Setter";
  const RoleS  = isRecruiter ? "Recruiter" : "Setter";

  return (
    <AnimatePresence>
      <motion.div
        key="staff-agreement-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={{ duration: 0.25 }}
          className="relative flex flex-col"
          style={{ width: "min(780px, 96vw)", maxHeight: "92vh", background: CARD, border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* ── Header ── */}
          <div style={{ background: DARK, borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "18px 24px", display: "flex", alignItems: "center", gap: "14px", flexShrink: 0 }}>
            <div style={{ width: "36px", height: "36px", background: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FileText size={18} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: ACCENT, margin: 0 }}>Independent Contractor Agreement</p>
              <p style={{ fontSize: "15px", fontWeight: 900, letterSpacing: "0.04em", textTransform: "uppercase", color: "#fff", margin: "2px 0 0" }}>{Role}</p>
              <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", margin: "1px 0 0" }}>Hylas Companion Group · Free as a Bird · {EFFECTIVE_DATE}</p>
            </div>
            <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
              <span style={{ fontSize: "8px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", padding: "3px 8px", border: `1px solid ${GOLD}`, color: GOLD, background: "rgba(245,200,66,0.08)" }}>Free as a Bird</span>
              <span style={{ fontSize: "8px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", padding: "3px 8px", border: "1px solid rgba(74,222,128,0.4)", color: "#4ADE80", background: "rgba(74,222,128,0.06)" }}>{isRecruiter ? "25% Weekly Flex" : "15% Weekly Flex"}</span>
            </div>
          </div>

          {/* ── Scrollable content ── */}
          <div ref={scrollRef} style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>

            {/* Parties */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
              <div style={{ padding: "12px 14px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
                <p style={{ fontSize: "8px", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: ACCENT, margin: "0 0 6px" }}>Company / Client</p>
                <p style={{ fontSize: "11px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: 0 }}>
                  <strong style={{ color: "#fff" }}>Andrej Sasaroga DBA Hylas Companion</strong><br />
                  andrey.sebastian@hylascompanion.us<br />
                  (872) 224-4442 · hylascompanion.com
                </p>
              </div>
              <div style={{ padding: "12px 14px", border: `1px solid rgba(255,77,0,0.2)`, background: "rgba(255,77,0,0.04)" }}>
                <p style={{ fontSize: "8px", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: ACCENT, margin: "0 0 6px" }}>Contractor</p>
                <p style={{ fontSize: "11px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: 0 }}>
                  <strong style={{ color: "#fff" }}>{staffName || "______________________"}</strong><br />
                  Role: {Role}<br />
                  Effective: {EFFECTIVE_DATE}
                </p>
              </div>
            </div>

            {/* Section: Freedom */}
            <Section title="1. Independent Contractor — Free as a Bird">
              <p style={pStyle}>Company engages Contractor as an independent contractor to perform <strong style={{ color: "#fff" }}>{isRecruiter ? "full-cycle recruiting services" : "appointment setting services"}</strong> for CDL-A drivers placed through the Hylas Companion Market. Contractor is <strong style={{ color: "#fff" }}>NOT</strong> an employee, agent, or partner of Company. You work when you want, from where you want, as much or as little as you want. No quota, no schedule, no minimum hours. Free to have other jobs, other clients, other businesses. Free as a bird.</p>
            </Section>

            {/* Section: Compensation */}
            <Section title={`2. Compensation — 3 Plans${isRecruiter ? " (Our Leads 50% · Your Leads 75%)" : " ($396–$726 Standard · 15% Weekly Flex)"}`}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                <PlanCard color="#111" label="A" title="Standard 50/50" sub={isRecruiter ? "Our Leads: 50%\nYour Leads: 75%" : "Dry Van $396 → $594\nReefer $462 → $660\nFlatbed $528 → $726 ✦"} />
                <PlanCard color="#7a5f00" label="B" title="One-Time Payment" sub={isRecruiter ? "Reduced bonus $100–$300\npaid immediately after carrier payment" : "Reduced bonus $50–$200\npaid immediately after carrier payment"} />
                <PlanCard color={ACCENT} label="C" title="Weekly Flex" sub={isRecruiter ? "25% of carrier weekly rate\n$17.50–$35.00 per week\nPassive · Recurring" : "15% of carrier weekly rate\n$10.50–$21.00 per week\nPassive · Recurring"} />
              </div>
              <div style={{ background: "#111", padding: "12px 14px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                  <div><p style={dbLbl}>Driver Earns</p><p style={{ fontSize: "18px", fontWeight: 900, color: "#4ADE80", margin: 0 }}>10%</p><p style={dbSub}>of weekly rate</p></div>
                  <div style={{ borderLeft: "1px solid rgba(255,255,255,0.08)", paddingLeft: "12px" }}><p style={dbLbl}>Setter Earns</p><p style={{ fontSize: "18px", fontWeight: 900, color: "#FFA500", margin: 0 }}>15%</p><p style={dbSub}>per week</p></div>
                  <div style={{ borderLeft: "1px solid rgba(255,255,255,0.08)", paddingLeft: "12px" }}><p style={dbLbl}>Recruiter Earns</p><p style={{ fontSize: "18px", fontWeight: 900, color: GOLD, margin: 0 }}>25%</p><p style={dbSub}>per week</p></div>
                </div>
              </div>
              <p style={{ ...pStyle, marginTop: "8px" }}>
                <strong style={{ color: GOLD }}>Milestone Bonuses:</strong> +$200 one-time on your first hired driver (any plan) · +$1,000 when 6+ drivers hired in a single calendar month · +$2,000 when 10+ drivers hired in a single calendar month.
                Paid weekly (Flex) or immediately (Standard) via Wise / Payoneer / Bank transfer. You are responsible for your own taxes.
              </p>
            </Section>

            {/* Section: Non-solicitation */}
            <Section title="3. Confidentiality & Non-Solicitation (12 months)">
              <ul style={{ paddingLeft: "16px", margin: "4px 0", color: "rgba(255,255,255,0.65)", fontSize: "11px", lineHeight: 1.8 }}>
                {isRecruiter ? <>
                  <li>May not recruit Hylas drivers for competing placement services.</li>
                  <li>May not approach Hylas partner carriers to offer competing placement services.</li>
                  <li>Must maintain strict confidentiality of driver profiles, carrier contacts, pay rates, the Hylas Matrix, scripts, and all Market data.</li>
                  <li>Not authorized to offer referral bonuses to drivers — Hylas operates an exclusive in-house driver referral program.</li>
                </> : <>
                  <li>May not approach Hylas carrier contacts for competing services.</li>
                  <li>Must maintain confidentiality of carrier lists, pricing, the Hylas Matrix, and sales methodology.</li>
                  <li>Not authorized to offer referral bonuses to drivers — Hylas operates an exclusive in-house driver referral program.</li>
                </>}
              </ul>
            </Section>

            {/* Section: Term */}
            <Section title="4. Term & Governing Law">
              <p style={pStyle}>At-will agreement, effective {EFFECTIVE_DATE}. Either party may terminate via email with {isRecruiter ? "14" : "7"} days written notice. Governed by Illinois law. Binding arbitration, individual basis only. No severance — commissions already earned and pending are always paid. <em style={{ color: "rgba(255,255,255,0.4)" }}>Free as a bird — you leave when you want.</em></p>
            </Section>

            {/* ── Checkboxes ── */}
            <div style={{ margin: "20px 0 0", padding: "16px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: ACCENT, margin: "0 0 12px" }}>Required Acknowledgments — Read carefully</p>
              {CHECKBOXES.map(c => (
                <label key={c.id} style={{ display: "flex", gap: "10px", marginBottom: "10px", cursor: "pointer", alignItems: "flex-start" }}>
                  <div
                    onClick={() => setChecked(p => ({ ...p, [c.id]: !p[c.id] }))}
                    style={{
                      width: "16px", height: "16px", flexShrink: 0, marginTop: "1px",
                      border: `1px solid ${checked[c.id] ? "#4ADE80" : "rgba(255,255,255,0.2)"}`,
                      background: checked[c.id] ? "rgba(74,222,128,0.15)" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                    data-testid={`staff-agreement-check-${c.id}`}
                  >
                    {checked[c.id] && <CheckCircle2 size={10} color="#4ADE80" />}
                  </div>
                  <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.65)", lineHeight: 1.65 }}>{c.label}</span>
                </label>
              ))}
            </div>

            {/* ── Signature block ── */}
            <div style={{ marginTop: "16px", padding: "16px", background: allChecked ? "rgba(255,77,0,0.04)" : "rgba(255,255,255,0.01)", border: `1px solid ${allChecked ? "rgba(255,77,0,0.25)" : "rgba(255,255,255,0.05)"}`, transition: "all 0.2s" }}>
              <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: allChecked ? ACCENT : "rgba(255,255,255,0.2)", margin: "0 0 10px", transition: "color 0.2s" }}>
                E-Signature — Clickwrap (ESIGN Act 15 U.S.C. § 7001)
              </p>

              <div style={{ marginBottom: "10px" }}>
                <label style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.15em", color: "rgba(255,255,255,0.35)", display: "block", marginBottom: "5px" }}>Type Your Full Legal Name to Sign</label>
                <input
                  data-testid="staff-sig-name-input"
                  value={sigName}
                  onChange={e => setSigName(e.target.value)}
                  placeholder="Your full legal name"
                  disabled={!allChecked}
                  style={{
                    width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${sigName.length >= 2 ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.1)"}`,
                    color: "#fff", fontSize: "13px", fontStyle: "italic", outline: "none",
                    opacity: allChecked ? 1 : 0.3, transition: "all 0.2s",
                  }}
                />
              </div>

              <label style={{ display: "flex", gap: "10px", cursor: "pointer", alignItems: "flex-start", opacity: allChecked ? 1 : 0.3, transition: "opacity 0.2s" }}>
                <div
                  onClick={() => allChecked && setSignConsent(p => !p)}
                  style={{
                    width: "16px", height: "16px", flexShrink: 0, marginTop: "1px",
                    border: `1px solid ${signConsent ? "#4ADE80" : "rgba(255,255,255,0.2)"}`,
                    background: signConsent ? "rgba(74,222,128,0.15)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: allChecked ? "pointer" : "default", transition: "all 0.15s",
                  }}
                  data-testid="staff-sign-consent-check"
                >
                  {signConsent && <CheckCircle2 size={10} color="#4ADE80" />}
                </div>
                <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.55)", lineHeight: 1.65 }}>
                  I have read and agree to this Independent {RoleS} Agreement in its entirety. I understand this is a legally binding contract and that my typed name above constitutes my electronic signature under the ESIGN Act.
                </span>
              </label>

              {canSign && (
                <div style={{ marginTop: "12px", padding: "8px 12px", background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", fontSize: "10px", color: "rgba(74,222,128,0.8)" }}>
                  <Shield size={10} style={{ display: "inline", marginRight: "5px" }} />
                  Signing as <strong style={{ color: "#4ADE80" }}>{sigName}</strong> · {EFFECTIVE_DATE} · IP will be captured at signing
                </div>
              )}
            </div>
          </div>

          {/* ── Footer / Actions ── */}
          <div style={{ padding: "14px 24px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: "10px", justifyContent: "flex-end", flexShrink: 0, background: DARK }}>
            <p style={{ flex: 1, fontSize: "10px", color: "rgba(255,255,255,0.25)", alignSelf: "center", margin: 0 }}>
              {!allChecked ? `${CHECKBOXES.filter(c => !checked[c.id]).length} acknowledgment(s) remaining` : !sigName ? "Type your full name above" : !signConsent ? "Check the consent box above" : ""}
            </p>
            <button
              data-testid="staff-agreement-sign-btn"
              onClick={handleSign}
              disabled={!canSign || submitting}
              style={{
                padding: "9px 22px", background: canSign ? ACCENT : "rgba(255,255,255,0.06)",
                color: canSign ? "#fff" : "rgba(255,255,255,0.2)", fontSize: "10px", fontWeight: 800,
                letterSpacing: "0.15em", textTransform: "uppercase", border: "none", cursor: canSign ? "pointer" : "not-allowed",
                transition: "all 0.2s",
              }}
            >
              {submitting ? "Signing…" : `I Agree & Sign as ${RoleS}`}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", margin: "0 0 6px", paddingBottom: "5px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>{title}</p>
      {children}
    </div>
  );
}

function PlanCard({ color, label, title, sub }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ background: color, padding: "5px 10px", display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{ width: "16px", height: "16px", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "8px", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{label}</span>
        <span style={{ fontSize: "8px", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff" }}>{title}</span>
      </div>
      <div style={{ padding: "8px 10px" }}>
        {sub.split("\n").map((line, i) => (
          <p key={i} style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)", margin: "0 0 2px", lineHeight: 1.5 }}>{line}</p>
        ))}
      </div>
    </div>
  );
}

const pStyle  = { fontSize: "11px", color: "rgba(255,255,255,0.65)", lineHeight: 1.7, margin: "0 0 6px" };
const dbLbl   = { fontSize: "7px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", margin: "0 0 2px" };
const dbSub   = { fontSize: "9px", color: "rgba(255,255,255,0.35)", margin: "2px 0 0" };
